import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItem } from '../support/queries';
import { CacheKey } from '../types/cache-keys';
import { ReleaseNotesResponse, ReleaseVersionsResponse } from '../types/jira-release-notes';

// Always request GSPTD project
const JIRA_PROJECT = 'GSPTD';

interface UseReleaseVersionsParams {
  include_released?: boolean;
  include_unreleased?: boolean;
  include_archived?: boolean;
  maxResults?: number;
}

/**
 * Fetches all Jira versions/releases for the GSPTD project from the dev Jira Cloud instance.
 * Ordered newest first by the backend.
 */
export const useReleaseVersions = (
  params: UseReleaseVersionsParams = {},
  options: Record<string, any> = {}
) => {
  return useQuery<ReleaseVersionsResponse>({
    queryKey: [CacheKey.JiraReleaseVersions, params],
    queryFn: () =>
      fetchItem('jira-cloud/release-notes/versions', {
        project: JIRA_PROJECT,
        ...params,
      }),
    ...{
        retry: false,
        staleTime: 1000 * 60 * 5,
        ...options,
      },
  });
};

interface UseReleaseNotesByVersionParams {
  maxResults?: number;
  issue_types?: string;
  force_refresh?: boolean;
}

/**
 * Fetches version metadata and all associated release note issues for a
 * specific Jira version ID from the dev Jira Cloud instance.
 */
export const useReleaseNotesByVersion = (
  versionId: string | null,
  params: UseReleaseNotesByVersionParams = {},
  options: Record<string, any> = {}
) => {
  return useQuery<ReleaseNotesResponse>({
    queryKey: [CacheKey.JiraReleaseNotes, versionId, params],
    queryFn: () =>
      fetchItem(`jira-cloud/release-notes/versions/${versionId}`, {
        project: JIRA_PROJECT,
        ...params,
      }),
    ...{
        enabled: !!versionId,
        retry: false,
        staleTime: 1000 * 60 * 5,
        ...options,
      },
  });
};

/**
 * Fetches release note issues by fix version name. This avoids relying on the
 * version-id specific backend path when that path is backed by a deprecated Jira search API.
 */
export const useReleaseNotesByFixVersion = (
  fixVersion: string | null,
  params: UseReleaseNotesByVersionParams = {},
  options: Record<string, any> = {}
) => {
  return useQuery<ReleaseNotesResponse>({
    queryKey: [CacheKey.JiraReleaseNotes, 'fix-version', fixVersion, params],
    queryFn: () =>
      fetchItem('jira-cloud/release-notes', {
        project: JIRA_PROJECT,
        fix_version: fixVersion,
        ...params,
      }),
    ...{
        enabled: !!fixVersion,
        retry: false,
        staleTime: 1000 * 60 * 5,
        ...options,
      },
  });
};



